import moment from "moment";
import { getQueryVariables } from "@/lib/url";

export const TIME_FORMAT = "hh:mm a";

function nextHalfHour(baseDate?: Date | string) {
  const startsAt = moment();

  // prefer the explicitly passed date; fall back to the date from the query
  // string so unthreaded callers and deep links keep working
  const base = baseDate || getQueryVariables().start;

  if (base) {
    const target = moment(base);
    if (target.isValid()) {
      startsAt.set({
        year: target.year(),
        month: target.month(),
        date: target.date()
      });
    }
  }

  // round up to the nearest half hour
  const remainder = 30 - (startsAt.minute() % 30);
  return startsAt.add(remainder, "minutes");
}

export enum Weekday {
  sunday = 0,
  monday = 1,
  tuesday = 2,
  wednesday = 3,
  thursday = 4,
  friday = 5,
  saturday = 6
}

export enum RecurrenceRuleType {
  DailyRule = "DailyRule",
  WeeklyRule = "WeeklyRule",
  MonthlyRule = "MonthlyRule",
  YearlyRule = "YearlyRule"
}

export enum RecurrencePresetName {
  daily = "daily",
  weekdays = "weekdays",
  weekly = "weekly",
  biweekly = "biweekly",
  monthly = "monthly",
  yearly = "yearly",
  custom = "custom"
}

export class ParticipantAttributes {
  participant_type: "User";
  participant_id: number;
}

export class Participants {
  user_id: number;
  name: string;
}

export class RecurrenceRule {
  type: RecurrenceRuleType;
  interval: number;
  days?: Array<number>;
  occurrence?: Array<number>;
  ends?: "never" | "on" | "after" = "never";
  until?: Date;
  count?: number;

  constructor(params: Partial<RecurrenceRule>) {
    Object.assign(this, params);
  }
}

export class CalendarEvent {
  id: number | null;
  name = "";
  exclusive = true;
  all_day = false;
  starts_at!: Date;
  ends_at!: Date;
  recurring = false;
  participants_attributes: ParticipantAttributes[] = [];
  participants?: Participants[];
  recurrence_preset?: RecurrencePresetName = RecurrencePresetName.weekly;
  recurrence_rules: RecurrenceRule[] = [];

  constructor(params: Partial<CalendarEvent> = {}) {
    Object.assign(this, params);
    // Defaulted after assignment so a passed starts_at also anchors the
    // derived ends_at (a field initializer would derive it from the default)
    this.starts_at ??= nextHalfHour().toDate();
    this.ends_at ??= moment(this.starts_at).add(30, "minutes").toDate();
  }

  get technicianIds() {
    return this.participants_attributes
      .filter(
        (attrs: ParticipantAttributes) => attrs.participant_type === "User"
      )
      .map((attrs: ParticipantAttributes) => attrs.participant_id);
  }

  get startTime() {
    return moment(this.starts_at).format(TIME_FORMAT);
  }

  get endTime() {
    return moment(this.ends_at).format(TIME_FORMAT);
  }
}

// Builds a new event whose default date is anchored to the given date
export function newCalendarEventOn(defaultDate?: Date | string): CalendarEvent {
  return new CalendarEvent({ starts_at: nextHalfHour(defaultDate).toDate() });
}
